import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient, queryKeys } from "../../lib/api";
import { useAuth } from "../../lib/auth";

/** Handles story upload: progress, success, and cache invalidation. No UI. */
export function useStoryUpload() {
  const { token, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);

  const uploadFormData = useCallback(
    async (formData: FormData) => {
      try {
        await apiClient.post("/stories", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000,
          onUploadProgress: (e) => {
            if (e.total) {
              setUploadProgress(Math.round((e.loaded * 100) / e.total));
            }
          },
        });
        setUploadProgress(100);
        queryClient.invalidateQueries({ queryKey: queryKeys.stories });
        queryClient.refetchQueries({ queryKey: queryKeys.stories });
        if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: "CACHE_UPDATE",
            action: "invalidate",
          });
        }
      } finally {
        setUploading(false);
      }
    },
    [queryClient]
  );

  const resetUpload = useCallback(() => {
    setUploading(false);
    setUploadProgress(0);
    setUploadStartTime(null);
  }, []);

  const startUpload = useCallback(() => {
    setUploading(true);
    setUploadProgress(0);
    setUploadStartTime(Date.now());
  }, []);

  return {
    token,
    isAuthenticated,
    uploading,
    setUploading,
    uploadProgress,
    uploadStartTime,
    uploadFormData,
    resetUpload,
    startUpload,
  };
}
